import React, { useMemo, useState } from 'react';
import { useAppData } from '@/context/AppContext';
import Card from '@/components/Card';
import AIInsightCard from '@/components/AIInsightCard';
import { Trash2, TrendingDown, TrendingUp, Activity, Award, Search, Shield, Calendar, MapPin, X } from 'lucide-react';
import styles from './ProgressPage.module.css';

function getPainColor(level: number): string {
  if (level <= 3) return 'var(--success)';
  if (level <= 6) return 'var(--tertiary)';
  return 'var(--error)';
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDayLabel(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const STAGE_META = [
  { name: 'Diagnosis', Icon: Search, color: '#D8A7A0' },
  { name: 'Protection', Icon: Shield, color: '#E6D3A3' },
  { name: 'Rehab', Icon: TrendingUp, color: '#A8C3A0' },
  { name: 'Return to Sport', Icon: Award, color: '#2F6F4F' },
] as const;

function RecoveryJourney({ settings, appointments, recoveryDays, onDeleteStage }: { settings: any; appointments: any[]; recoveryDays: number; onDeleteStage?: (index: number) => void }) {
  const recoveryStartDate = settings.recoveryStartDate
    ? new Date(settings.recoveryStartDate)
    : new Date(Date.now() - recoveryDays * 86400000);

  let stageHistory: { stage: number; date: string }[] = [];
  try { if (settings.stageHistory) stageHistory = JSON.parse(settings.stageHistory); } catch {}

  const now = new Date();

  const weekLabel = (date: Date) => {
    const days = Math.floor((date.getTime() - recoveryStartDate.getTime()) / 86400000);
    if (days < 0) return 'Before recovery';
    return `Week ${Math.floor(days / 7) + 1}`;
  };

  type TItem = { id: string; date: Date; title: string; subtitle: string; Icon: any; color: string; isPast: boolean; type: 'stage' | 'appointment'; originalIndex?: number };

  const stageItems: TItem[] = stageHistory.map((entry, i) => {
    const meta = STAGE_META[entry.stage as 0 | 1 | 2 | 3] || STAGE_META[0];
    const date = new Date(entry.date);
    return { id: `stage-${i}`, date, title: meta.name, subtitle: weekLabel(date), Icon: meta.Icon, color: meta.color, isPast: date <= now, type: 'stage', originalIndex: i };
  });

  const apptItems: TItem[] = appointments.map((apt: any) => {
    const date = new Date(apt.date);
    return { id: `apt-${apt.id}`, date, title: apt.title, subtitle: weekLabel(date), Icon: Calendar, color: '#B12612', isPast: date < now, type: 'appointment' };
  });

  const timeline = [...stageItems, ...apptItems].sort((a, b) => a.date.getTime() - b.date.getTime());

  if (timeline.length === 0) {
    return (
      <Card className={styles.insightsCard}>
        <h2 className={styles.sectionTitle}>Recovery Journey</h2>
        <div className={styles.emptyTimeline}>
          <MapPin size={28} style={{ color: 'var(--text-secondary)', marginBottom: 12 }} />
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
            Your journey will appear here as you progress through stages and add appointments.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.insightsCard}>
      <h2 className={styles.sectionTitle}>Recovery Journey</h2>
      <div className={styles.journeyTimeline}>
        {timeline.map((item, index) => {
          const isLast = index === timeline.length - 1;
          const { Icon } = item;
          return (
            <div key={item.id} className={styles.journeyItem}>
              <div className={styles.journeyLeft}>
                <div
                  className={styles.journeyDot}
                  style={{ background: item.isPast ? item.color : 'var(--bg-secondary)', borderColor: item.isPast ? item.color : 'var(--border)' }}
                >
                  <Icon size={12} color={item.isPast ? '#fff' : 'var(--text-secondary)'} />
                </div>
                {!isLast && (
                  <div className={styles.journeyLine} style={{ background: item.isPast ? item.color + '40' : 'var(--border)' }} />
                )}
              </div>
              <div className={`${styles.journeyContent} ${!isLast ? styles.journeyContentPadded : ''}`}>
                <div className={styles.journeyRow}>
                  <span
                    className={styles.journeyWeekBadge}
                    style={{ background: item.isPast ? item.color + '20' : 'var(--bg-secondary)', color: item.isPast ? item.color : 'var(--text-secondary)' }}
                  >
                    {item.subtitle}
                  </span>
                  <span className={styles.journeyDate}>
                    {item.date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    {item.type === 'appointment' ? ` · ${item.date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : ''}
                  </span>
                  {item.type === 'stage' && onDeleteStage && item.originalIndex !== undefined && (
                    <button
                      className={styles.journeyDeleteBtn}
                      onClick={() => onDeleteStage(item.originalIndex!)}
                      title="Remove this stage"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className={styles.journeyTitle} style={{ color: item.isPast ? item.color : 'var(--text)' }}>{item.title}</div>
                <div className={styles.journeyType}>{item.type === 'stage' ? 'Recovery stage' : 'Appointment'}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function ProgressPage() {
  const {
    painLogs,
    exerciseCompletions,
    prescribedExercises,
    meals,
    appointments,
    settings,
    addPainLog,
    deletePainLog,
    setSetting,
  } = useAppData();

  const today = new Date();
  const todayStr = toDateStr(today);

  // ── Pain chart edit state ──
  type EditDay = { date: Date; log: { id: string; level: number } | null };
  const [editDay, setEditDay] = useState<EditDay | null>(null);
  const [editLevel, setEditLevel] = useState(5);
  const [saving, setSaving] = useState(false);

  const recoveryStartDate = useMemo(() => {
    const s = settings.recoveryStartDate;
    return s ? new Date(s) : new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  }, [settings.recoveryStartDate]);

  const getWeekLabel = (date: Date) => {
    const days = Math.floor((date.getTime() - recoveryStartDate.getTime()) / 86400000);
    if (days < 0) return 'W0';
    return `W${Math.floor(days / 7) + 1}`;
  };

  const openEditDay = (date: Date, log: { id: string; level: number } | null) => {
    setEditLevel(log ? log.level : 5);
    setEditDay({ date, log });
  };

  const handleSave = async () => {
    if (!editDay) return;
    setSaving(true);
    try {
      const noon = new Date(editDay.date);
      noon.setHours(12, 0, 0, 0);
      if (editDay.log) {
        await deletePainLog(editDay.log.id);
      }
      await addPainLog(editLevel, '', '', noon);
      setEditDay(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!editDay?.log) return;
    setSaving(true);
    try {
      await deletePainLog(editDay.log.id);
      setEditDay(null);
    } finally {
      setSaving(false);
    }
  };

  const last7Days = useMemo(() => {
    const days: Date[] = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      days.push(d);
    }
    return days;
  }, [todayStr]);

  const painByDay = useMemo(() => {
    return last7Days.map(day => {
      const dayStr = toDateStr(day);
      const logsForDay = painLogs
        .filter(p => toDateStr(new Date(p.timestamp)) === dayStr)
        .sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const top = logsForDay.length > 0 ? logsForDay[0] : null;
      return {
        date: day,
        level: top ? top.level : null,
        log: top ? { id: top.id, level: top.level } : null,
      };
    });
  }, [last7Days, painLogs]);

  const exerciseByDay = useMemo(() => {
    return last7Days.map(day => {
      const dayStr = toDateStr(day);
      const jsDay = day.getDay();
      const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
      const scheduled = prescribedExercises.filter((e: any) => {
        const days: number[] = Array.isArray(e.scheduleDays) ? e.scheduleDays : [];
        return days.includes(dayIdx);
      }).length;
      const completed = exerciseCompletions.filter((c: any) => c.date === dayStr).length;
      return {
        date: day,
        completed,
        scheduled,
        pct: scheduled > 0 ? Math.min(1, completed / scheduled) : null,
      };
    });
  }, [last7Days, exerciseCompletions, prescribedExercises]);

  const weekStart = useMemo(() => {
    const d = new Date(today);
    const dow = d.getDay();
    const diff = dow === 0 ? 6 : dow - 1;
    d.setDate(d.getDate() - diff);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [todayStr]);

  const weeklyStats = useMemo(() => {
    const weekStartStr = toDateStr(weekStart);
    const weekPainLogs = painLogs.filter(p => toDateStr(new Date(p.timestamp)) >= weekStartStr && toDateStr(new Date(p.timestamp)) <= todayStr);
    const avgPain = weekPainLogs.length > 0
      ? (weekPainLogs.reduce((sum: number, p: any) => sum + p.level, 0) / weekPainLogs.length).toFixed(1)
      : '-';

    const weekExercises = exerciseCompletions.filter((c: any) => c.date >= weekStartStr && c.date <= todayStr).length;

    const weekMeals = meals.filter(m => {
      const mStr = toDateStr(new Date(m.timestamp));
      return mStr >= weekStartStr && mStr <= todayStr;
    });
    const totalProtein = weekMeals.reduce((sum: number, m: any) => sum + (m.protein || 0), 0);

    return { avgPain, weekExercises, totalProtein };
  }, [painLogs, exerciseCompletions, meals, weekStart, todayStr]);

  const recoveryDays = useMemo(() => {
    const startStr = settings.recoveryStartDate;
    const startDate = startStr ? new Date(startStr) : new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  }, [settings.recoveryStartDate, todayStr]);

  const insights = useMemo(() => {
    const result: { text: string; color: string; icon: 'up' | 'down' | 'activity' | 'star' }[] = [];

    const thisWeekPain = painLogs.filter(p => toDateStr(new Date(p.timestamp)) >= toDateStr(weekStart) && toDateStr(new Date(p.timestamp)) <= todayStr);
    const lastWeekStart = new Date(weekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(weekStart);
    lastWeekEnd.setDate(lastWeekEnd.getDate() - 1);
    const lastWeekPain = painLogs.filter(p => {
      const pStr = toDateStr(new Date(p.timestamp));
      return pStr >= toDateStr(lastWeekStart) && pStr <= toDateStr(lastWeekEnd);
    });

    if (thisWeekPain.length > 0 && lastWeekPain.length > 0) {
      const avgThis = thisWeekPain.reduce((s: number, p: any) => s + p.level, 0) / thisWeekPain.length;
      const avgLast = lastWeekPain.reduce((s: number, p: any) => s + p.level, 0) / lastWeekPain.length;
      const change = ((avgLast - avgThis) / avgLast * 100).toFixed(0);
      if (Number(change) > 0) {
        result.push({ text: `Your pain has decreased ${change}% compared to last week`, color: 'var(--success)', icon: 'down' });
      } else if (Number(change) < 0) {
        result.push({ text: `Your pain has increased ${Math.abs(Number(change))}% compared to last week`, color: 'var(--error)', icon: 'up' });
      } else {
        result.push({ text: 'Your pain levels are stable compared to last week', color: 'var(--accent)', icon: 'activity' });
      }
    }

    const weekExCount = exerciseCompletions.filter((c: any) => c.date >= toDateStr(weekStart) && c.date <= todayStr).length;
    result.push({ text: `You completed ${weekExCount} exercise${weekExCount !== 1 ? 's' : ''} this week`, color: 'var(--secondary)', icon: 'activity' });

    if (recoveryDays > 0) {
      result.push({ text: `You are ${recoveryDays} days into your recovery journey`, color: 'var(--primary)', icon: 'star' });
    }

    return result;
  }, [painLogs, exerciseCompletions, weekStart, todayStr, recoveryDays]);

  const sortedPainLogs = useMemo(() => {
    return [...painLogs].sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 20);
  }, [painLogs]);

  const painAverage7 = useMemo(() => {
    const weekStartStr = toDateStr(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000));
    const recentLogs = painLogs.filter(p => toDateStr(new Date(p.timestamp)) >= weekStartStr);
    if (recentLogs.length === 0) return 0;
    return recentLogs.reduce((s: number, p: any) => s + p.level, 0) / recentLogs.length;
  }, [painLogs, todayStr]);

  const todayProtein = useMemo(() => {
    return meals.filter(m => toDateStr(new Date(m.timestamp)) === todayStr).reduce((s: number, m: any) => s + (m.protein || 0), 0);
  }, [meals, todayStr]);

  const proteinGoal = parseInt(settings.dailyProteinGoal ?? '120', 10);

  const exerciseCompletionPct = useMemo(() => {
    const todayDow = new Date().getDay();
    const scheduledToday = (prescribedExercises ?? []).filter((e: any) => {
      const days: number[] = Array.isArray(e.scheduleDays) ? e.scheduleDays : [];
      return days.includes(todayDow);
    });
    if (scheduledToday.length === 0) return 0;
    const completed = exerciseCompletions.filter((c: any) => c.date === todayStr).length;
    return Math.min(100, Math.round((completed / scheduledToday.length) * 100));
  }, [prescribedExercises, exerciseCompletions, todayStr]);

  const hasExerciseData = exerciseByDay.some(d => d.scheduled > 0);

  const stageWeeks: number[] = useMemo(() => {
    try { return settings.stageWeeks ? JSON.parse(settings.stageWeeks) : [2, 6, 6, 4]; } catch { return [2, 6, 6, 4]; }
  }, [settings.stageWeeks]);
  const manualRecoveryStage = parseInt(settings.manualRecoveryStage ?? '0', 10);

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Progress</h1>

      {/* ── Stage Stepper ── */}
      <Card className={styles.chartCard}>
        <h2 className={styles.sectionTitle}>Your Recovery Journey</h2>
        <div className={styles.stepperRow}>
          {STAGE_META.map((stage, idx) => {
            const isActive = idx === manualRecoveryStage;
            const isPast = idx < manualRecoveryStage;
            const isLast = idx === STAGE_META.length - 1;
            const weeks = stageWeeks[idx] ?? 4;
            return (
              <div key={stage.name} className={styles.stepperItem}>
                <div className={styles.stepperCircleRow}>
                  {idx > 0 ? (
                    <div
                      className={styles.stepperLine}
                      style={{ background: isPast || isActive ? STAGE_META[idx - 1].color : 'var(--bg-tertiary)' }}
                    />
                  ) : <div className={styles.stepperLineSpacer} />}
                  <div
                    className={`${styles.stepperCircle} ${isActive ? styles.stepperCircleActive : ''}`}
                    style={{
                      background: isActive || isPast
                        ? `linear-gradient(135deg, ${stage.color}ee, ${stage.color}88)`
                        : 'var(--bg-secondary)',
                      border: isActive || isPast ? 'none' : '2px solid var(--border)',
                    }}
                  >
                    <stage.Icon size={16} color={isActive || isPast ? '#fff' : 'var(--text-secondary)'} />
                  </div>
                  {!isLast ? (
                    <div
                      className={styles.stepperLine}
                      style={{ background: isPast ? stage.color : 'var(--bg-tertiary)' }}
                    />
                  ) : <div className={styles.stepperLineSpacer} />}
                </div>
                <div
                  className={styles.stepperWeeks}
                  style={{ color: isActive ? stage.color : isPast ? stage.color + 'aa' : 'var(--text-secondary)' }}
                >
                  {weeks}w
                </div>
                <div
                  className={styles.stepperLabel}
                  style={{ color: isActive ? 'var(--text)' : 'var(--text-secondary)', fontWeight: isActive ? 700 : 400 }}
                >
                  {stage.name}
                </div>
              </div>
            );
          })}
        </div>
        {(() => {
          const ActiveIcon = STAGE_META[manualRecoveryStage].Icon;
          return (
        <div
          className={styles.currentStageCard}
          style={{ background: STAGE_META[manualRecoveryStage].color + '22', border: `1px solid ${STAGE_META[manualRecoveryStage].color}55` }}
        >
          <div
            className={styles.currentStageIcon}
            style={{ background: `linear-gradient(135deg, ${STAGE_META[manualRecoveryStage].color}cc, ${STAGE_META[manualRecoveryStage].color}88)` }}
          >
            <ActiveIcon size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>You are in {STAGE_META[manualRecoveryStage].name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              {stageWeeks[manualRecoveryStage]}w assigned · Day {recoveryDays} of recovery
            </div>
          </div>
        </div>
          );
        })()}
      </Card>

      <AIInsightCard
        painAverage={painAverage7}
        exerciseCompletion={exerciseCompletionPct}
        proteinIntake={todayProtein}
        proteinGoal={proteinGoal}
        daysInRecovery={recoveryDays}
      />

      <Card className={styles.chartCard}>
        <div className={styles.chartCardHeader}>
          <h2 className={styles.sectionTitle} style={{ margin: 0 }}>Pain Trend (Last 7 Days)</h2>
          <span className={styles.chartHint}>Click a bar to add or edit</span>
        </div>
        <div className={styles.chartArea}>
          <div className={styles.yAxis}>
            <span>10</span>
            <span>8</span>
            <span>6</span>
            <span>4</span>
            <span>2</span>
            <span>0</span>
          </div>
          <div className={styles.barsArea}>
            {painByDay.map((d, i) => {
              const isToday = toDateStr(d.date) === todayStr;
              return (
                <button
                  key={i}
                  className={`${styles.chartBar} ${styles.chartBarBtn}`}
                  onClick={() => openEditDay(d.date, d.log)}
                  title={d.level !== null ? `Pain: ${d.level} — click to edit` : 'Click to log pain'}
                >
                  {d.level !== null ? (
                    <>
                      <span className={styles.barValue}>{d.level}</span>
                      <div
                        className={styles.barFill}
                        style={{ height: `${(d.level / 10) * 100}%`, background: getPainColor(d.level) }}
                      />
                    </>
                  ) : (
                    <div
                      className={styles.emptyCircle}
                      style={isToday ? { borderColor: 'var(--primary)', background: 'color-mix(in srgb, var(--primary) 8%, transparent)' } : undefined}
                    />
                  )}
                  <span className={styles.barLabel} style={isToday ? { color: 'var(--primary)', fontWeight: 700 } : undefined}>
                    {d.date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      <Card className={styles.chartCard}>
        <h2 className={styles.sectionTitle}>Exercise Completion (Last 7 Days)</h2>
        {hasExerciseData ? (
          <div className={styles.chartArea}>
            <div className={styles.yAxis}>
              <span>100%</span>
              <span>75%</span>
              <span>50%</span>
              <span>25%</span>
              <span>0%</span>
            </div>
            <div className={styles.barsArea}>
              {exerciseByDay.map((d, i) => (
                <div key={i} className={styles.chartBar}>
                  {d.pct !== null ? (
                    <>
                      <span className={styles.barValue}>{Math.round(d.pct * 100)}%</span>
                      <div
                        className={styles.barFill}
                        style={{
                          height: `${d.pct * 100}%`,
                          background: d.pct >= 1
                            ? 'var(--success)'
                            : d.pct >= 0.5
                            ? 'var(--secondary)'
                            : 'var(--tertiary)',
                        }}
                      />
                    </>
                  ) : (
                    <div className={styles.barFill} style={{ height: '4px', background: 'var(--bg-tertiary)' }} />
                  )}
                  <span className={styles.barLabel}>{d.date.toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 3)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className={styles.noData}>Add exercises to your schedule to track completion</p>
        )}
      </Card>

      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--primary)' }}>{weeklyStats.avgPain}</div>
          <div className={styles.statLabel}>Avg Pain This Week</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--secondary)' }}>{weeklyStats.weekExercises}</div>
          <div className={styles.statLabel}>Exercises This Week</div>
        </Card>
        <Card className={styles.statCard}>
          <div className={styles.statValue} style={{ color: 'var(--accent)' }}>{weeklyStats.totalProtein}g</div>
          <div className={styles.statLabel}>Protein This Week</div>
        </Card>
      </div>

      <RecoveryJourney
        settings={settings}
        appointments={appointments}
        recoveryDays={recoveryDays}
        onDeleteStage={async (index) => {
          try {
            const current: { stage: number; date: string }[] = settings.stageHistory ? JSON.parse(settings.stageHistory) : [];
            await setSetting('stageHistory', JSON.stringify(current.filter((_, i) => i !== index)));
          } catch {}
        }}
      />

      {insights.length > 0 ? (
        <Card className={styles.insightsCard}>
          <h2 className={styles.sectionTitle}>Recovery Insights</h2>
          {insights.map((insight, i) => (
            <div key={i} className={styles.insightItem}>
              <div className={styles.insightIcon} style={{ background: `${insight.color}22`, color: insight.color }}>
                {insight.icon === 'down' ? <TrendingDown size={16} /> :
                 insight.icon === 'up' ? <TrendingUp size={16} /> :
                 insight.icon === 'activity' ? <Activity size={16} /> :
                 <Award size={16} />}
              </div>
              <span className={styles.insightText}>{insight.text}</span>
            </div>
          ))}
        </Card>
      ) : null}

      <Card>
        <h2 className={styles.sectionTitle}>Pain Log History</h2>
        {sortedPainLogs.length > 0 ? (
          <div className={styles.painLogList}>
            {sortedPainLogs.map((log: any) => (
              <div key={log.id} className={styles.painLogItem}>
                <div className={styles.painLogInfo}>
                  <div
                    className={styles.painLevel}
                    style={{ background: `${getPainColor(log.level)}22`, color: getPainColor(log.level) }}
                  >
                    {log.level}
                  </div>
                  <div>
                    <div className={styles.painLogLocation}>{log.location || 'General'}</div>
                    {log.notes ? <div className={styles.painLogDate} style={{ fontStyle: 'italic' }}>{log.notes}</div> : null}
                    <div className={styles.painLogDate}>{formatDate(new Date(log.timestamp))}</div>
                  </div>
                </div>
                <button className={styles.deleteBtn} onClick={() => deletePainLog(log.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.noData}>No pain logs recorded yet</p>
        )}
      </Card>

      {/* ── Pain Edit Modal ── */}
      {editDay !== null ? (
        <div className={styles.modalOverlay} onClick={() => setEditDay(null)}>
          <div className={styles.editModal} onClick={e => e.stopPropagation()}>
            <button className={styles.modalClose} onClick={() => setEditDay(null)}><X size={18} /></button>
            <h3 className={styles.editModalTitle}>
              {editDay.log ? 'Edit Pain Entry' : 'Log Pain'}
            </h3>
            <p className={styles.editModalDate}>
              {editDay.date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
              {' · '}{getWeekLabel(editDay.date)}
            </p>

            <div className={styles.levelRow}>
              <button
                className={styles.levelBtn}
                onClick={() => setEditLevel(l => Math.max(1, l - 1))}
              >−</button>
              <div
                className={styles.levelCircle}
                style={{ background: `${getPainColor(editLevel)}20`, borderColor: getPainColor(editLevel) }}
              >
                <span className={styles.levelNum} style={{ color: getPainColor(editLevel) }}>{editLevel}</span>
                <span className={styles.levelLabel} style={{ color: getPainColor(editLevel) }}>
                  {editLevel <= 3 ? 'Low' : editLevel <= 6 ? 'Moderate' : 'High'}
                </span>
              </div>
              <button
                className={styles.levelBtn}
                onClick={() => setEditLevel(l => Math.min(10, l + 1))}
              >+</button>
            </div>

            <div className={styles.dotRow}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  className={styles.dotPip}
                  style={{ background: n <= editLevel ? getPainColor(editLevel) : 'var(--bg-tertiary)' }}
                  onClick={() => setEditLevel(n)}
                />
              ))}
            </div>

            <div className={styles.editActions}>
              {editDay.log ? (
                <button
                  className={styles.deleteActionBtn}
                  onClick={handleDeleteEntry}
                  disabled={saving}
                >
                  <Trash2 size={14} /> Delete
                </button>
              ) : <div />}
              <button
                className={styles.saveActionBtn}
                style={{ background: getPainColor(editLevel) }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : editDay.log ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
